/** @jsxImportSource @emotion/react */

import { useEffect, useState } from "react";
import {
  Checkbox,
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PALETTE } from "styles/base/variables";
import {
  InsuranceKind,
  InsuranceReportPanel,
  InsuranceReportView
} from "models/payrollManagement.model";
import { patchInsuranceFiling } from "pages/remotes";
import { LEAVE_REASON_LABEL } from "../SeveranceRequest";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // §2.15 패널 (부모가 조회해서 내려준다). null = 로딩 전/실패
  panel: InsuranceReportPanel | null;
  // 처음 열 탭 (그리드 취득/상실 칸 클릭 시 해당 탭)
  initialTab?: InsuranceKind;
  // 그리드 칸에서 열면 그 거래처만 보여준다. null = 전체
  businessId?: string | null;
  // 완료/해제 직후 부모가 패널을 다시 조회하도록 (그리드는 닫을 때 한 번만 갱신)
  onChanged: () => void;
}

// 담당자 조치가 필요한 건 = 아직 처리 안 됨. 그리드 칸은 거래처 요청 여부와 관계없이
// 기간 안 직원 전원으로 판정하므로(§3.1) 요청 없는 직원도 포함한다.
// 회사 직접 신고(locked)는 filed=true로 내려와 자연히 빠진다.
export const isInsurancePending = (v: InsuranceReportView) => !v.filed;

const modeLabel = (v: InsuranceReportView) =>
  v.mode === "REQUEST"
    ? "신고 요청"
    : v.mode === "SELF_FILED"
    ? "회사 직접 신고"
    : "요청 없음";

const fmtDateTime = (s: string | null | undefined) =>
  s ? s.slice(0, 16).replace("T", " ") : "";

const fmtWon = (n: number | null | undefined) =>
  n === null || n === undefined ? "-" : `${n.toLocaleString()}원`;

const lossReasonLabel = (v: InsuranceReportView) => {
  const code = v.lossReasonCode?.trim();
  const text = v.lossReasonText?.trim() || (code && LEAVE_REASON_LABEL[code]);
  if (code && text) return `${code} ${text}`;
  return text || code || "-";
};

function InsuranceReportDialog({
  isOpen,
  onClose,
  panel,
  initialTab = "ACQUISITION",
  businessId = null,
  onChanged
}: Props) {
  const [tab, setTab] = useState<InsuranceKind>(initialTab);
  const [bizFilter, setBizFilter] = useState<string | null>(businessId);
  // 요청 중인 행 (businessId+payeeId+kind)
  const [busy, setBusy] = useState<Set<string>>(new Set());
  // 저장은 됐지만 패널 재조회 전인 행의 filed 값. 재조회 전에 체크가 원래대로 튀거나
  // 같은 값을 한 번 더 보내지 않게 한다. 새 패널이 오면 비운다.
  const [filedOverride, setFiledOverride] = useState<Map<string, boolean>>(
    new Map()
  );

  useEffect(() => {
    setFiledOverride(new Map());
  }, [panel]);

  useEffect(() => {
    if (!isOpen) return;
    setTab(initialTab);
    setBizFilter(businessId);
  }, [isOpen, initialTab, businessId]);

  const byBiz = (list: InsuranceReportView[]) =>
    bizFilter ? list.filter((v) => v.businessId === bizFilter) : list;
  const acqList = byBiz(panel?.acquisitions ?? []);
  const lossList = byBiz(panel?.losses ?? []);
  const list = tab === "ACQUISITION" ? acqList : lossList;
  const filterName =
    bizFilter &&
    [...(panel?.acquisitions ?? []), ...(panel?.losses ?? [])].find(
      (v) => v.businessId === bizFilter
    )?.businessName;

  const rowKey = (v: InsuranceReportView, kind: InsuranceKind) =>
    `${v.businessId}:${v.payeeId}:${kind}`;

  const isFiled = (v: InsuranceReportView, kind: InsuranceKind) =>
    filedOverride.get(rowKey(v, kind)) ?? v.filed;

  const toggleFiled = (v: InsuranceReportView, kind: InsuranceKind) => {
    if (v.locked) return;
    const key = rowKey(v, kind);
    if (busy.has(key)) return;
    const next = !isFiled(v, kind);
    setBusy((prev) => new Set(prev).add(key));
    patchInsuranceFiling({
      businessId: v.businessId,
      payeeId: v.payeeId,
      kind,
      filed: next
    })
      .then(() => {
        setFiledOverride((prev) => new Map(prev).set(key, next));
        onChanged();
      })
      .catch((err) => {
        console.error(err);
        alert(
          err.response?.data?.message ||
            "공단 신고 처리 중 오류가 발생했습니다."
        );
      })
      .finally(() =>
        setBusy((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        })
      );
  };

  const thCss = css`
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: ${PALETTE.textMid};
    padding: 0 10px 6px 0;
    border-bottom: 1px solid ${PALETTE.borderLight};
    white-space: nowrap;
  `;
  const tdCss = css`
    padding: 9px 10px 9px 0;
    border-bottom: 1px solid ${PALETTE.borderLight};
    vertical-align: middle;
    font-size: 13px;
    line-height: 1.5;
  `;
  const subCss = css`
    display: block;
    font-size: 11px;
    color: ${PALETTE.textMid};
  `;

  const filedCell = (v: InsuranceReportView, kind: InsuranceKind) => (
    <span
      css={css`
        display: inline-flex;
        align-items: center;
        gap: 2px;
        white-space: nowrap;
      `}
    >
      <Checkbox
        size="small"
        checked={isFiled(v, kind)}
        disabled={v.locked || busy.has(rowKey(v, kind))}
        onChange={() => toggleFiled(v, kind)}
        css={css`
          padding: 2px;
        `}
      />
      <span>
        {v.locked ? "직접 신고" : isFiled(v, kind) ? "신고 완료" : "미신고"}
        {isFiled(v, kind) && v.filedAt && (
          <span css={subCss}>
            {fmtDateTime(v.filedAt)} {v.filedByUser || ""}
          </span>
        )}
      </span>
    </span>
  );

  const requestCell = (v: InsuranceReportView) => (
    <td
      css={tdCss}
      style={{
        color: v.mode === null ? PALETTE.textMid : undefined,
        fontWeight: v.mode === "REQUEST" ? 600 : undefined
      }}
    >
      {modeLabel(v)}
      {v.requestedAt && (
        <span css={subCss}>{fmtDateTime(v.requestedAt)}</span>
      )}
    </td>
  );

  const rowCss = (v: InsuranceReportView) =>
    isInsurancePending(v)
      ? css`
          background: ${PALETTE.primary}0d;
        `
      : undefined;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        css={css`
          font-size: 16px;
          font-weight: 700;
        `}
      >
        취득/상실신고 요청
        <span
          css={css`
            font-weight: 400;
            font-size: 12px;
            color: ${PALETTE.textMid};
            margin-left: 8px;
          `}
        >
          거래처가 세특 [지급대상자]에서 보낸 4대보험 취득·상실 신고 요청입니다
        </span>
        <IconButton
          onClick={onClose}
          css={css`
            position: absolute;
            right: 8px;
            top: 8px;
          `}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
            border-bottom: 1px solid ${PALETTE.borderLight};
            margin-bottom: 12px;
          `}
        >
          {(
            [
              [
                "ACQUISITION",
                `취득신고 ${acqList.filter(isInsurancePending).length}`
              ],
              ["LOSS", `상실신고 ${lossList.filter(isInsurancePending).length}`]
            ] as [InsuranceKind, string][]
          ).map(([t, label]) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              css={css`
                border: 0;
                background: transparent;
                font: inherit;
                font-size: 13px;
                font-weight: 600;
                cursor: pointer;
                padding: 7px 12px 9px;
                color: ${tab === t ? PALETTE.text : PALETTE.textMid};
                border-bottom: 2px solid
                  ${tab === t ? PALETTE.primary : "transparent"};
              `}
            >
              {label}
            </button>
          ))}
          {bizFilter && (
            <span
              css={css`
                margin-left: auto;
                font-size: 12px;
                color: ${PALETTE.textMid};
              `}
            >
              <b
                css={css`
                  color: ${PALETTE.text};
                `}
              >
                {filterName || "선택한 거래처"}
              </b>
              만 보는 중 ·{" "}
              <button
                type="button"
                onClick={() => setBizFilter(null)}
                css={css`
                  border: 0;
                  background: transparent;
                  font: inherit;
                  color: ${PALETTE.primary};
                  cursor: pointer;
                  padding: 0;
                  text-decoration: underline;
                `}
              >
                전체 보기
              </button>
            </span>
          )}
        </div>

        <p
          css={css`
            margin: 0 0 8px;
            font-size: 12px;
            color: ${PALETTE.textMid};
          `}
        >
          {tab === "ACQUISITION" ? "취득일(입사일)" : "상실일(퇴사일 다음날)"}이
          이번 달 또는 전월인 직원입니다. 공단 EDI에서 신고한 뒤 체크하면 표의{" "}
          <b>{tab === "ACQUISITION" ? "취득" : "상실"}</b> 칸에 반영됩니다. 거래처
          요청이 없는 직원도 표 칸 판정에 들어가므로 직접 확인해 처리해야
          하며, 회사가 직접 신고한 건은 완료로 고정됩니다.
        </p>

        <table
          css={css`
            width: 100%;
            border-collapse: collapse;
          `}
        >
          <thead>
            {tab === "ACQUISITION" ? (
              <tr>
                <th css={thCss}>거래처</th>
                <th css={thCss}>직원</th>
                <th css={thCss}>취득일</th>
                <th css={thCss}>보수월액</th>
                <th css={thCss}>주 소정근로</th>
                <th css={thCss}>직종</th>
                <th css={thCss}>계약직</th>
                <th css={thCss}>요청</th>
                <th css={thCss}>신고 처리</th>
              </tr>
            ) : (
              <tr>
                <th css={thCss}>거래처</th>
                <th css={thCss}>직원</th>
                <th css={thCss}>입사일</th>
                <th css={thCss}>퇴사일</th>
                <th css={thCss}>상실일</th>
                <th css={thCss}>상실사유</th>
                <th css={thCss}>요청</th>
                <th css={thCss}>신고 처리</th>
              </tr>
            )}
          </thead>
          <tbody>
            {!panel ? (
              <tr>
                <td css={tdCss} colSpan={9} style={{ color: PALETTE.textMid }}>
                  불러오는 중입니다.
                </td>
              </tr>
            ) : !list.length ? (
              <tr>
                <td css={tdCss} colSpan={9} style={{ color: PALETTE.textMid }}>
                  {tab === "ACQUISITION"
                    ? "취득신고 대상 직원이 없습니다."
                    : "상실신고 대상 직원이 없습니다."}
                </td>
              </tr>
            ) : tab === "ACQUISITION" ? (
              list.map((v) => (
                <tr key={rowKey(v, "ACQUISITION")} css={rowCss(v)}>
                  <td css={tdCss}>
                    <b>{v.businessName}</b>
                  </td>
                  <td css={tdCss}>{v.payeeName}</td>
                  <td css={tdCss}>{v.hireDate || "-"}</td>
                  <td css={tdCss}>{fmtWon(v.monthlyRemuneration)}</td>
                  <td css={tdCss}>
                    {v.weeklyWorkHour != null ? `${v.weeklyWorkHour}시간` : "-"}
                  </td>
                  <td css={tdCss}>
                    {v.occupation || "-"}
                    {v.occupationCode && (
                      <span css={subCss}>부호 {v.occupationCode}</span>
                    )}
                  </td>
                  <td css={tdCss}>
                    {v.contractWorker
                      ? `예${
                          v.contractEndMonth
                            ? ` (~${v.contractEndMonth})`
                            : ""
                        }`
                      : v.contractWorker === false
                      ? "아니오"
                      : "-"}
                  </td>
                  {requestCell(v)}
                  <td css={tdCss}>{filedCell(v, "ACQUISITION")}</td>
                </tr>
              ))
            ) : (
              list.map((v) => (
                <tr key={rowKey(v, "LOSS")} css={rowCss(v)}>
                  <td css={tdCss}>
                    <b>{v.businessName}</b>
                  </td>
                  <td css={tdCss}>{v.payeeName}</td>
                  <td css={tdCss}>{v.hireDate || "-"}</td>
                  <td css={tdCss}>{v.leaveDate || "-"}</td>
                  <td css={tdCss}>{v.lossDate || "-"}</td>
                  <td css={tdCss}>{lossReasonLabel(v)}</td>
                  {requestCell(v)}
                  <td css={tdCss}>{filedCell(v, "LOSS")}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}

export default InsuranceReportDialog;
